import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { Loader2, ArrowRight } from "lucide-react";

export default function RegisterPage() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", company: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user.id) navigate("/app", { replace: true });
  }, [user, navigate]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const payload = { ...form };
    if (!payload.company) delete payload.company;
    const res = await register(payload);
    setLoading(false);
    if (!res.ok) setError(res.error);
    else navigate("/app", { replace: true });
  };

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden font-inter">
      <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
      <div className="absolute -bottom-40 -right-32 h-[500px] w-[500px] rounded-full bg-brand-purple/15 blur-3xl" />

      <div className="relative z-10 max-w-md mx-auto px-6 pt-16 pb-20">
        <Link to="/" className="inline-block mb-12">
          <Logo size="md" />
        </Link>
        <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-brand-green mb-3">
          Nouveau compte
        </div>
        <h1 className="text-4xl sm:text-5xl font-black tracking-tighter leading-none text-white">
          Inscription
        </h1>
        <p className="mt-4 text-sm text-brand-gray font-roboto leading-relaxed">
          Créez votre compte pour accéder au catalogue privé et au programme B2B.
        </p>

        <form onSubmit={handleSubmit} className="mt-10 space-y-4" data-testid="register-form">
          <Field label="Nom complet" testId="register-name-input" value={form.name} onChange={update("name")} required />
          <Field label="E-mail" type="email" testId="register-email-input" value={form.email} onChange={update("email")} required />
          <Field label="Mot de passe (6 car. min)" type="password" testId="register-password-input" value={form.password} onChange={update("password")} required minLength={6} />
          <Field label="Entreprise (optionnel)" testId="register-company-input" value={form.company} onChange={update("company")} />

          {error && (
            <div className="text-sm text-red-400 font-roboto" data-testid="register-error">{error}</div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="register-submit-button"
            className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-sm bg-brand-green text-black font-bold tracking-wide hover:bg-brand-green/90 disabled:opacity-50 transition shadow-glow-green"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Créer le compte <ArrowRight className="h-4 w-4" /></>}
          </button>
        </form>

        <p className="mt-8 text-sm text-brand-gray font-roboto">
          Déjà inscrit ?{" "}
          <Link to="/login" className="text-brand-green hover:underline" data-testid="link-to-login">Se connecter</Link>
        </p>
      </div>
    </div>
  );
}

function Field({ label, testId, ...props }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.25em] font-bold text-brand-gray mb-2">
        {label}
      </span>
      <input
        {...props}
        data-testid={testId}
        className="w-full h-12 px-4 bg-brand-navy/50 border border-brand-blue/60 rounded-sm text-white placeholder-white/30 focus:outline-none focus:border-brand-green focus:bg-brand-navy/80 font-roboto transition"
      />
    </label>
  );
}
